import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAppDispatch } from 'app/hooks';
import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import Error404 from 'components/navigation/Error404';
import { IPublication } from 'features/publication/publicationAPI';
import {
  deletePublicationAsync,
  getPublicationAnchorId,
  patchPublicationAsync,
} from 'features/publication/publicationSlice';
import { useUser } from 'selectors/userSelectors';

import classNames from 'classnames';
import FileUpload from 'components/files/FileUpload';
import {
  doFormMsg,
  FormMessage,
  FormMsg,
  NoFormMsg,
} from 'components/forms/FormMessage';
import {
  MarkdownContent,
  MarkdownExplainer,
} from 'components/markdown/MarkdownContent';
import { useConfirmDelete } from 'components/modal/forms/ConfirmDeleteForm';
import LiveContentWarning from 'components/page/LiveContentWarning';
import { IFile } from 'features/files/fileAPI';
import { imageFileTypes } from 'features/files/fileSlice';
import { canEdit } from 'features/user/userSlice';
import { PUBLICATION_STATUSES } from 'helpers/consts';
import { sortBy } from 'helpers/sorting';
import { Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useSite } from 'selectors/siteSelectors';
import { useUsers } from 'selectors/usersSelectors';
import '../static/StaticPage.scss';
import './../page/Pages.scss';

type PublicationProps = {
  publication: IPublication;
  className?: string;
};

function toDateInputValue(value?: Date | string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function formatPublicationDate(
  value: Date | string | null | undefined,
  locale: string,
) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

function normalizePublicationDownload(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const currentOrigin = window.location.origin;
  try {
    const parsedUrl = new URL(trimmed);
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) return null;
    if (parsedUrl.origin === currentOrigin) {
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }
    return parsedUrl.toString();
  } catch {
    try {
      const parsedPath = new URL(trimmed, currentOrigin);
      if (parsedPath.origin !== currentOrigin) return null;
      return `${parsedPath.pathname}${parsedPath.search}${parsedPath.hash}`;
    } catch {
      return null;
    }
  }
}

export default function Publication(props: PublicationProps) {
  const { publication, className } = props;

  const { locale, user, userIsManager, userIsAdmin } = useUser();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { publications } = useSite();
  const { users } = useUsers();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [saved, setSaved] = useState<boolean>(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(
    publication.image?.uri ?? null,
  );

  const [editing, setEditing] = useState<boolean>(false);
  const [notfound, setNotfound] = useState<boolean>(false);
  const confirmDelete = useConfirmDelete();
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const [titleHasContent, setTitleHasContent] = useState<boolean>(
    Boolean(publication.title),
  );
  const [textHasContent, setTextHasContent] = useState<boolean>(
    Boolean(publication.text),
  );
  const formMessageContainerRef = useRef<HTMLDivElement | null>(null);
  const anchorId = getPublicationAnchorId(publication);
  const [randomSide] = useState<'left' | 'right'>(
    Math.random() > 0.5 ? 'left' : 'right',
  );
  const editable = useMemo(
    () => canEdit(user, publication),
    [user, publication],
  );
  const sortedUsers = useMemo(() => {
    const u = sortBy(users, 'username');
    return u;
  }, [users]);

  useEffect(() => {
    if (editable && !publication.title) {
      setEditing(true);
    }
  }, [editable, publication.title]);

  useEffect(() => {
    setTitleHasContent(Boolean(publication.title));
  }, [publication.title]);

  useEffect(() => {
    setTextHasContent(Boolean(publication.text));
  }, [publication.text]);

  useEffect(() => {
    if (
      formMsg.message !==
      intl.formatMessage({
        id: 'GENERAL.UNSAVED_IMG',
      })
    ) {
      return;
    }
    formMessageContainerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [formMsg.message, intl]);

  const updateTitle = useCallback(
    (e: React.FocusEvent<HTMLHeadingElement>) => {
      const target = e.currentTarget;
      dispatch(
        patchPublicationAsync({
          data: {
            ...publication,
            title: target.textContent ?? '',
          } as IPublication,
          file: file,
          publication: publication,
        }),
      );
    },
    [dispatch, publication, file],
  );

  const handleTitleInput = useCallback(
    (e: React.InputEvent<HTMLHeadingElement>) => {
      setTitleHasContent((e.currentTarget.textContent ?? '').length > 0);
    },
    [],
  );

  const handleAuthorChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedAuthor = users.find(
        (user) => user.id === Number(e.currentTarget.value),
      );
      if (!selectedAuthor) return;
      dispatch(
        patchPublicationAsync({
          data: {
            ...publication,
            author: selectedAuthor,
          } as IPublication,
        }),
      );
    },
    [dispatch, publication, users],
  );

  const updateText = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      dispatch(
        patchPublicationAsync({
          data: {
            ...publication,
            text: e.currentTarget.value ?? '',
          } as IPublication,
        }),
      );
    },
    [dispatch, publication],
  );

  const handleTextInput = useCallback(
    (e: React.InputEvent<HTMLTextAreaElement>) => {
      setTextHasContent((e.currentTarget.value ?? '').length > 0);
    },
    [],
  );

  const updateDownload = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      const normalizedDownload = normalizePublicationDownload(
        e.currentTarget.textContent ?? '',
      );
      if (normalizedDownload === null) return;
      dispatch(
        patchPublicationAsync({
          data: {
            ...publication,
            download: normalizedDownload,
          } as IPublication,
        }),
      );
    },
    [dispatch, publication],
  );

  const updatePublicationDate = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      dispatch(
        patchPublicationAsync({
          data: {
            ...publication,
            publicationDate: new Date(e.currentTarget.value),
            status: 'published',
          } as IPublication,
        }),
      );
    },
    [dispatch, publication],
  );

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      dispatch(
        patchPublicationAsync({
          data: {
            ...publication,
            status: e.currentTarget.value as IPublication['status'],
          } as IPublication,
        }),
      );
    },
    [dispatch, publication],
  );

  const handleRemovePublication = useCallback(() => {
    function doDelete(): boolean {
      dispatch(
        deletePublicationAsync({
          id: publication.id!,
        }),
      ).then((response: any) => {
        switch (response.payload?.response.status) {
          case 204:
            break;
          case 500:
          default:
            console.log(response);
        }
      });
      return true;
    }
    confirmDelete({
      prompts: {
        prompt1: (
          <FormattedMessage
            id="PUBLICATIONS.DELETE_CONFIRM"
            values={{ publication: publication.title }}
          />
        ),
        prompt2: <FormattedMessage id="PUBLICATIONS.DELETE_CONFIRM_2" />,
      },
      again: { serious: true },
    }).then((confirmed: boolean) => {
      if (confirmed) doDelete();
    });
  }, [dispatch, publication, confirmDelete]);

  const handleImageSelect = useCallback<
    Dispatch<SetStateAction<File | undefined>>
  >((file) => {
    setFile(file);
    if (file)
      setSaved(false); // mark as not saved when a new file is selected
    else setSaved(true);
  }, []);

  const handleImageUpload = useCallback(() => {
    dispatch(
      patchPublicationAsync({
        data: {
          ...publication,
        } as IPublication,
        file: file,
        publication: publication,
      }),
    ).then((response: any) => {
      switch (response.payload?.response.status) {
        case 200:
          setSaved(true);
          doFormMsg(
            {
              message: intl.formatMessage({
                id: 'GENERAL.IMAGE_UPLOAD_SUCCESS',
              }),
              success: true,
              timeout: 5000,
            },
            setFormMsg,
          );
          break;
        case 500:
        default:
          doFormMsg(
            {
              message: intl.formatMessage({
                id: 'GENERAL.IMAGE_UPLOAD_FAILED',
              }),
              success: false,
            },
            setFormMsg,
          );
          break;
      }
    });
  }, [dispatch, publication, file]);

  const handleDeleteImage = useCallback(() => {
    dispatch(
      patchPublicationAsync({
        data: {
          ...publication,
          image: null,
        } as IPublication,
        publication: publication,
      }),
    );
  }, [dispatch, publication]);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = event.target;
      dispatch(
        patchPublicationAsync({
          data: {
            ...publication,
            [name]: checked,
          } as IPublication,
          publication: publication,
        }),
      );
    },
    [dispatch, publication],
  );

  const handleToggleEditing = useCallback(() => {
    if (editing && saved !== true && file) {
      doFormMsg(
        {
          message: intl.formatMessage({
            id: 'GENERAL.UNSAVED_IMG',
          }),
          success: false,
          timeout: 5000,
        },
        setFormMsg,
      );
      return;
    }
    setEditing((currentEditing) => !currentEditing);
  }, [editing, intl, saved, file]);

  const isEditing = editable && editing;

  console.log('publication:', publication);
  return (
    <div className="item_container">
      <div
        className={classNames(`publication ${className ?? ''}`, {
          editing: isEditing,
        })}
      >
        {notfound || !publication ? (
          <Error404 />
        ) : (
          <>
            {isEditing ? (
              <div id={anchorId}>
                <LiveContentWarning editing={editing} />
                {userIsManager ? (
                  <select
                    name="publication_author"
                    className="page_author_select"
                    value={publication.author?.id ?? ''}
                    onChange={handleAuthorChange}
                  >
                    <option value="" disabled>
                      {intl.formatMessage({ id: 'GENERAL.AUTHOR' })}
                    </option>
                    {sortedUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.username}
                      </option>
                    ))}
                  </select>
                ) : null}
                <section className="page_header">
                  <h2
                    className={classNames('page_title', {
                      plch: !titleHasContent,
                    })}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={intl.formatMessage({
                      id: 'PUBLICATIONS.TITLE_PLCH',
                    })}
                    onInput={handleTitleInput}
                    onBlur={updateTitle}
                  >
                    {publication.title ?? ''}
                  </h2>
                </section>
                <textarea
                  name={`citation-${publication.id}`}
                  className="page_text"
                  defaultValue={publication.text ?? ''}
                  placeholder={intl.formatMessage({
                    id: 'PUBLICATIONS.TEXT_PLCH',
                  })}
                  onInput={handleTextInput}
                  onBlur={updateText}
                />
                <MarkdownExplainer />
                <div className="settings_label">
                  <FormattedMessage id="GENERAL.DOWNLOAD" />
                </div>
                <div
                  className="page_text"
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={updateDownload}
                >
                  {publication.download ?? ''}
                </div>
                <div className="settings_label">
                  <FormattedMessage id="GENERAL.STATUS.STATUS" />
                </div>
                <div className="item_dates">
                  <div className="settings_label inline">
                    <FormattedMessage id="GENERAL.STATUS.STATUS" />
                  </div>
                  <select
                    className="page_author_select"
                    value={publication.status}
                    onChange={handleStatusChange}
                  >
                    {PUBLICATION_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {intl.formatMessage({
                          id: `GENERAL.STATUS.${status}`.toUpperCase(),
                        })}
                      </option>
                    ))}
                  </select>
                  <div className="settings_label inline">
                    <FormattedMessage id="PUBLICATIONS.PUBLICATION_DATE" />
                  </div>
                  <input
                    className="page_text"
                    type="date"
                    defaultValue={toDateInputValue(publication.publicationDate)}
                    onBlur={updatePublicationDate}
                  />
                </div>
                <div className="settings_label">
                  <FormattedMessage id="GENERAL.IMAGE" />
                </div>
                <div
                  ref={formMessageContainerRef}
                  className="form_message_container"
                >
                  <FormMessage {...formMsg} />
                </div>
                <div className="image_settings">
                  <div
                    className={classNames('page_image editing', {
                      image_contain: !!publication.image_contain,
                    })}
                  >
                    <FileUpload
                      types={imageFileTypes}
                      currentSrc={publication.image?.uri || null}
                      previewSrc={previewSrc}
                      size={200}
                      defaultImg={null}
                      setPreviewSrc={setPreviewSrc}
                      showTrigger={true}
                      showMsg={(m) => doFormMsg(m, setFormMsg)}
                      setFile={handleImageSelect}
                      onDelete={handleDeleteImage}
                      onSave={handleImageUpload}
                      saved={saved}
                      confirmationText={
                        <FormattedMessage id="GENERAL.DELETE_IMAGE_CNF" />
                      }
                      disabled={!editing}
                    />
                  </div>
                  <Form.Check
                    className="image_contain"
                    id={'contain'}
                    type="checkbox"
                    label={intl.formatMessage({ id: 'GENERAL.IMAGE_CONTAIN' })}
                    name="image_contain"
                    checked={!!publication.image_contain}
                    onChange={handleCheckboxChange}
                  />
                </div>
              </div>
            ) : (
              <>
                <section className="page_header" id={anchorId}>
                  <h2>{publication.title}</h2>
                </section>
                <section className="page_body">
                  {publication.image ? (
                    <div
                      className={classNames(`page_image ${randomSide}`, {
                        image_contain: !!publication.image_contain,
                      })}
                    >
                      <img
                        src={(publication.image as IFile).uri}
                        alt={publication.title}
                      />
                    </div>
                  ) : null}
                  <MarkdownContent className="page_text">
                    {publication.text}
                  </MarkdownContent>
                  {publication.download ? (
                    <div className="publication_download">
                      <Link
                        to={publication.download}
                        target="_blank"
                        reloadDocument
                        rel="noopener noreferrer"
                      >
                        <Icon symbol={IconSymbol.download} size={20} />
                        <FormattedMessage id="GENERAL.DOWNLOAD" />
                      </Link>
                    </div>
                  ) : null}
                </section>
                <section className="publication_footer">
                  {user ? (
                    <div className="meta_data">
                      {formatPublicationDate(
                        publication.publicationDate,
                        locale,
                      )}
                      {publication.status ? (
                        <>
                          {' · '}
                          <FormattedMessage
                            id={`GENERAL.STATUS.${publication.status.toUpperCase()}`}
                          />
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              </>
            )}
          </>
        )}
      </div>
      {user ? (
        <div className="section_menu">
          {editable ? (
            <>
              <Icon
                symbol={editing ? IconSymbol.visible : IconSymbol.pencil}
                onClick={handleToggleEditing}
                size={20}
                hintProps={{
                  hint: editing
                    ? { id: 'PAGES.PREVIEW_MODE' }
                    : { id: 'PAGES.EDIT_MODE' },
                }}
              />
              <Icon
                inline
                className="icon_button"
                symbol={IconSymbol.bin}
                size={18}
                onClick={() => handleRemovePublication()}
                hintProps={{ hint: { id: 'PUBLICATIONS.REMOVE_PUBLICATION' } }}
              />
            </>
          ) : null}
          {/* <Icon
            symbol={IconSymbol.plus}
            onClick={() => handleAddPublication()}
            size={18}
            hintProps={{ hint: { id: 'PUBLICATIONS.ADD_PUBLICATION' } }}
          /> */}
        </div>
      ) : null}
    </div>
  );
}
